import React from 'react';
import { PermissionNode } from '../useUserDetail';
import { ChevronRightIcon } from '../../../components/ui/Icons';
import styles from '../UserDetail.module.css';

interface PermissionsTreeProps {
  permissions: PermissionNode[];
  value: string[];
  onChange: (val: string[]) => void;
  disabled?: boolean;
}

const CheckMark = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const PermissionsTree: React.FC<PermissionsTreeProps> = ({ permissions, value, onChange, disabled }) => {
  const { roots, childrenMap } = React.useMemo(() => {
    const ids = new Set(permissions.map((p) => p.id));
    const rootsArr: PermissionNode[] = [];
    const map: Record<string, PermissionNode[]> = {};
    permissions.forEach((p) => (map[p.id] = []));
    permissions.forEach((p) => {
      if (p.father_id && ids.has(p.father_id)) map[p.father_id].push(p);
      else rootsArr.push(p);
    });
    return { roots: rootsArr, childrenMap: map };
  }, [permissions]);

  const [open, setOpen] = React.useState<Record<string, boolean>>({});

  const descendants = React.useCallback(
    (pid: string): string[] => {
      const kids = childrenMap[pid] || [];
      let acc: string[] = [];
      kids.forEach((k) => {
        acc.push(k.id);
        acc = acc.concat(descendants(k.id));
      });
      return acc;
    },
    [childrenMap]
  );

  const toggle = (id: string, checked: boolean) => {
    const set = new Set(value);
    if (checked) set.add(id);
    else set.delete(id);
    onChange(Array.from(set));
  };

  const selectAll = (pid: string, select: boolean) => {
    const all = [pid, ...descendants(pid)];
    const set = new Set(value);
    all.forEach((x) => (select ? set.add(x) : set.delete(x)));
    onChange(Array.from(set));
  };

  const renderNodes = (nodes: PermissionNode[], depth = 0): React.ReactNode =>
    nodes.map((node) => {
      const kids = childrenMap[node.id] || [];
      const hasKids = kids.length > 0;
      const desc = descendants(node.id);
      const selCount = desc.filter((d) => value.includes(d)).length;
      const allSelected = desc.length > 0 && desc.every((d) => value.includes(d)) && value.includes(node.id);
      const isOpen = !!open[node.id];
      const checked = value.includes(node.id);

      return (
        <div key={node.id} className={styles.permNode} style={{ paddingLeft: depth ? 18 : 0 }}>
          <div className={styles.permRow}>
            {hasKids ? (
              <button
                type="button"
                className={[styles.permChevron, isOpen ? styles.permChevronOpen : ''].join(' ')}
                onClick={() => setOpen((o) => ({ ...o, [node.id]: !o[node.id] }))}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
              >
                <ChevronRightIcon size={15} />
              </button>
            ) : (
              <span className={styles.permChevronSpacer} />
            )}

            <label className={styles.permLabel}>
              <input
                type="checkbox"
                className={styles.permCheckboxNative}
                checked={checked}
                disabled={disabled}
                onChange={(e) => toggle(node.id, e.target.checked)}
              />
              <span className={[styles.permBox, checked ? styles.permBoxChecked : ''].join(' ')}>
                {checked && <CheckMark />}
              </span>
              <span className={styles.permName}>{node.name}</span>
              {node.description && <span className={styles.permDesc}>— {node.description}</span>}
              {hasKids && selCount > 0 && <span className={styles.permBadge}>{selCount}</span>}
            </label>
          </div>

          {hasKids && isOpen && (
            <div className={styles.permChildren}>
              {renderNodes(kids, depth + 1)}
              {!disabled && (
                <div className={styles.permSelectAllRow}>
                  <button
                    type="button"
                    className={[styles.permSelectAll, allSelected ? styles.permSelectAllActive : ''].join(' ')}
                    onClick={() => selectAll(node.id, !allSelected)}
                  >
                    {allSelected ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    });

  if (permissions.length === 0) {
    return <p className={styles.permEmpty}>No permissions available for this role.</p>;
  }

  return <div className={styles.permTree}>{renderNodes(roots)}</div>;
};

export default PermissionsTree;
